import React, { useEffect, useRef, useState } from 'react';
import RsTable from '@Pages/RetroSynthesis/components/RsTable';
import PropTypes from 'prop-types';
import RsPaging from '@Pages/RetroSynthesis/components/RsPaging';
import cn from 'classnames';
import first from 'lodash/first';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { useHistory } from 'react-router-dom';
import RsModal from '@Pages/RetroSynthesis/components/RsModal';
import ProjectTableHeader from '@Pages/RetroSynthesis/RetroSynthesisProjects/components/ProjectTableHeader';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import Auth from '@Services/Auth';
import RsProjectTableToolBar from '@Pages/RetroSynthesis/RetroSynthesisProjects/components/RsProjectTableToolBar';
import { dispatchOpenImportFileModal } from '@Containers/Search/Shared/ImportFile/ImportFileModalActions';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { onViewResultsClick, onProjectDeleteClick, onProjectDeleteConfirm, onProjectParametersClick, onProjectEdit } from './analytics';
import DialogWithInfoLayout from '../../../Containers/Dialogs/DialogsWithIcon/DialogWithInfoLayout';
import { scrollTop, focusOnElement } from '../utils';
import { isAnyPredictiveRetrosynthesisAvailable } from '../predictiveUtils';
import ProjectTableItemWrapper from './components/ProjectTableItemWrapper';
import { getParentAndChildProjectsByIds } from './utils';

import './RetroSynthesisProjects.scss';

export const extractCurrentSortOption = (sort) => {
  const firstItem = first(sort);
  return firstItem && firstItem.property && firstItem.direction ? `${firstItem.property},${firstItem.direction}` : undefined;
};

export const RT_ANON_USER_POPUP_FLAG_ID = 'RTAnonUserPopup';
export const RT_COLLAPSE_PARAMS_FLAG_ID = 'RTCollapseParams';

export const getProjectById = (id, list = []) => first(list.filter((p) => p.id === id));
/* eslint-disable react-hooks/exhaustive-deps */
function RetroSynthesisProjects({
  response,
  isLoading,
  doDeleteProjects,
  doFetchProjects,
  doEditName,
  doSimilaritySearch,
  openSynthesisJobRetryDialog,
  doEditProject,
  goToLogin,
  doRetrySynthesisJob,
  doRetryMultipleSynthesisJob,
  lastViewedProjectPosition,
  setViewProjectsPosition,
}) {
  const history = useHistory();

  const { content = [], totalPages, size, number, totalElements, sort } = response || {};

  const tableRef = useRef();

  const [showAnonUserPopup, setShowAnonUserPopup] = useState(
    Auth.isAnonymousUser() && get(Auth.getUser(), `flags.${RT_ANON_USER_POPUP_FLAG_ID}`, true)
  );
  const [paramsCollapsed, setParamsCollapsed] = useState(get(Auth.getUser(), `flags.${RT_COLLAPSE_PARAMS_FLAG_ID}`, true));
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    let params = {};
    if (!isNil(number) && !isNil(sort)) {
      params = { page: number, sort: extractCurrentSortOption(sort) };
    }
    if (!isNil(size)) {
      params.size = size;
    }
    doFetchProjects(params);
  }, []);

  useEffect(() => {
    scrollTop(tableRef.current);
  }, [number]);

  useEffect(() => {
    if (response && totalElements === 0 && !showAnonUserPopup) {
      history.push(RsRoute.DRAW_STRUCTURE);
    } else if (response && totalElements > 0 && lastViewedProjectPosition && !isLoading) {
      focusOnElement(`project-status__view-results-button-${lastViewedProjectPosition.id}`);
      setViewProjectsPosition(null);
    }
  }, [response]);

  const handleChanges = (newParams) => {
    if (!newParams) {
      return;
    }
    // In case 'page' or changed, we should keep defined sorting option
    // In case or 'sort' or 'size' changed we should set page to 0
    if (newParams.page) {
      doFetchProjects({
        size,
        sort: extractCurrentSortOption(sort),
        ...newParams,
      });
    } else if (newParams.sort || newParams.size) {
      doFetchProjects({
        page: 0,
        size,
        ...newParams,
      });
    }
  };

  const viewResultsHandler = (projectVO) => {
    onViewResultsClick(projectVO);
    setViewProjectsPosition({ scrollTop: tableRef.current?.scrollTop, id: projectVO.id });
    history.push(RsRoute.getProjectLink(projectVO.id));
  };

  const selectionChangeHandler = (e) => {
    const { targetId, value } = e || {};
    if (!targetId || !selectedIds) {
      return;
    }
    if (value && selectedIds.indexOf(targetId) === -1) {
      setSelectedIds((ids) => [...ids, targetId]);
    } else if (!value) {
      setSelectedIds((ids) => ids.filter((id) => id !== targetId));
    }
  };

  const editHandler = (project) => {
    doEditProject(project);
    // AA
    onProjectEdit(project);
    history.push(RsRoute.DRAW_STRUCTURE);
  };

  const loginHandler = () => {
    goToLogin();
  };

  const closeAnonUserPopupHandler = () => {
    const flags = Auth.getUser()?.flags || {};
    flags[RT_ANON_USER_POPUP_FLAG_ID] = false;
    setShowAnonUserPopup(false);
    Auth.updateUser({ flags });
    if (response && totalElements === 0) {
      history.push(RsRoute.DRAW_STRUCTURE);
    }
  };

  const toggleParamsCollapsedHandler = () => {
    onProjectParametersClick(paramsCollapsed);
    const flags = Auth.getUser()?.flags || {};
    flags[RT_COLLAPSE_PARAMS_FLAG_ID] = !paramsCollapsed;
    setParamsCollapsed((prev) => !prev);
    Auth.updateUser({ flags });
  };

  const onDeleteConfirmationHandler = async (id) => {
    const ids = id || selectedIds;
    await doDeleteProjects(ids);
    const parentAndChildProjects = getParentAndChildProjectsByIds(content, ids);
    // AA
    onProjectDeleteConfirm({
      projects: parentAndChildProjects,
    });

    setSelectedIds([]);
    await doFetchProjects({ size, sort: extractCurrentSortOption(sort) });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onDeleteClickHandler = ({ type, id }) => {
    const ids = type === 'single' ? [id] : selectedIds;
    const parentAndChildProjects = getParentAndChildProjectsByIds(content, ids);
    // AA
    onProjectDeleteClick({
      projects: parentAndChildProjects,
    });

    let text = `Are you sure that you want to delete ${
      // eslint-disable-next-line sonarjs/no-nested-conditional
      ids.length === 1 ? 'this project' : `${ids.length} project${ids.length > 1 ? 's' : ''}`
    } ?`;
    let hasUncompleteGroups = false;
    for (let i = 0; i < ids.length; i += 1) {
      const projectId = ids[i];
      const childProjects = get(getProjectById(projectId, content), 'childProjects');
      if (childProjects && childProjects.some((child) => !ids.includes(child.id))) {
        hasUncompleteGroups = true;
        break;
      }
    }
    if (hasUncompleteGroups) {
      // if user delete parent project, which has child projects, that are not selected
      if (ids.length === 1) {
        text =
          'Deleting your original project will also remove the associated group for your extended projects. ' +
          'These projects will then be treated as individual items. Do you wish to proceed?';
      } else {
        const projectSuffix = ids.length > 1 ? 's' : '';
        text =
          'Deleting your original project will also remove the associated group for your extended projects. ' +
          'This means that those projects will be treated as individual items. ' +
          `Do you want to proceed with deleting ${ids.length} project${projectSuffix}?`;
      }
    }
    NotifyDialogsService.openModalWindow({
      title: 'Confirm deletion',
      text,
      size: 'md',
      cancelText: 'Cancel',
      confirmText: 'Yes',
      type: 'info',
      backdrop: 'static',
      onConfirm: () => onDeleteConfirmationHandler(ids),
    });
  };

  const onImportClickHandler = () => {
    dispatchOpenImportFileModal(true);
  };

  const isAllSelected =
    content.reduce(
      (totalCount, project) => (project?.childProjects?.length ? totalCount + project.childProjects.length + 1 : totalCount + 1),
      0
    ) === selectedIds.length;

  const onSelectAllClickHandler = () => {
    setSelectedIds(
      !isAllSelected
        ? content.reduce((ids, project) => {
            if (project.childProjects && project.childProjects.length) {
              ids.push(...project.childProjects.map((child) => child.id));
            }
            ids.push(project.id);
            return ids;
          }, [])
        : []
    );
  };

  return (
    <div className="retro-synthesis-projects">
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div className="retro-synthesis-projects__body" id="main-content" tabIndex="0">
        {!!showAnonUserPopup && (
          <RsModal showBackdrop>
            <DialogWithInfoLayout
              size={isAnyPredictiveRetrosynthesisAvailable() ? 'lg' : 'md'}
              character="!"
              layoutType="info"
              selfClose={closeAnonUserPopupHandler}
              onYesClick={loginHandler}
              yesLabel="Sign in">
              {isAnyPredictiveRetrosynthesisAvailable() ? (
                <div className="anon-notification--text">
                  You are using Reaxys as an anonymous user and, therefore, Predictive Retrosynthesis is not available for you and all your
                  projects will be deleted when you leave Reaxys.
                  <br />
                  Please login to keep your projects and to use Predictive Retrosynthesis.
                </div>
              ) : (
                <>
                  You are using Synthesis Planning as an anonymous user.
                  <br />
                  All projects will be deleted when you leave Reaxys.
                  <br />
                  Please login to keep your projects.
                </>
              )}
            </DialogWithInfoLayout>
          </RsModal>
        )}

        <RsProjectTableToolBar
          selectedCount={(selectedIds && selectedIds.length) || 0}
          onSelectAllClick={onSelectAllClickHandler}
          isAllSelected={isAllSelected}
          onDeleteClick={onDeleteClickHandler}
          onImport={onImportClickHandler}
        />
        <p id="date-span" className="sr-only">
          This column shows the date the project was created.
        </p>
        <p id="parameter-span" className="sr-only">
          These parameters are based on your settings from the previous page
        </p>
        <RsTable
          className={cn('rs-project-table')}
          isLoading={isLoading}
          showFooter
          dataList={content}
          renderItem={(projectVO) => (
            <ProjectTableItemWrapper
              project={projectVO}
              onEditName={async (id, newValue) => {
                await doEditName(id, newValue);
                await doFetchProjects({ size, sort: extractCurrentSortOption(sort) });
              }}
              selectedIds={selectedIds}
              onSelectionChange={selectionChangeHandler}
              showParams={!paramsCollapsed}
              onToggleParamsDisplay={toggleParamsCollapsedHandler}
              onSimilaritySearch={doSimilaritySearch}
              openSynthesisJobRetryDialog={openSynthesisJobRetryDialog}
              onViewResults={viewResultsHandler}
              onEdit={editHandler}
              onDelete={() => onDeleteClickHandler({ type: 'single', id: projectVO.id })}
              onRerun={doRetryMultipleSynthesisJob}
              doRetrySynthesisJob={doRetrySynthesisJob}
            />
          )}
          header={
            <ProjectTableHeader
              className="rs-project-table__header"
              sort={sort}
              onSortChange={handleChanges}
              showParams={!paramsCollapsed}
              onToggleParamsDisplay={toggleParamsCollapsedHandler}
            />
          }
          ref={tableRef}>
          <RsPaging pageSize={size} currentPage={number + 1} totalPages={totalPages} totalItems={totalElements} onChange={handleChanges} />
        </RsTable>
      </div>
    </div>
  );
}

RetroSynthesisProjects.defaultProps = {
  doFetchProjects: () => null,
  doEditName: () => null,
  doDeleteProjects: () => null,
  doSimilaritySearch: () => null,
  openSynthesisJobRetryDialog: () => null,
  doEditProject: () => null,
  goToLogin: () => null,
  doRetrySynthesisJob: () => null,
  doRetryMultipleSynthesisJob: () => null,
};

RetroSynthesisProjects.propTypes = {
  response: PropTypes.object,
  doFetchProjects: PropTypes.func,
  isLoading: PropTypes.bool,
  doEditName: PropTypes.func,
  doDeleteProjects: PropTypes.func,
  doSimilaritySearch: PropTypes.func,
  openSynthesisJobRetryDialog: PropTypes.func,
  doEditProject: PropTypes.func,
  goToLogin: PropTypes.func,
  doRetrySynthesisJob: PropTypes.func,
  doRetryMultipleSynthesisJob: PropTypes.func,
  lastViewedProjectPosition: PropTypes.object,
  setViewProjectsPosition: PropTypes.func,
};

export default RetroSynthesisProjects;
